import React, { useEffect, useMemo, useRef, useState } from 'react';
import Papa from 'papaparse';
import { usePost } from '../API';

type ParsedRow = { key: string, value: string };

const VIEW_PAGE_LIMIT = 10;

export function LookupTablePage() {
  const uploadLookup = usePost('/program/admin/upload_lookup');
  const listKinds = usePost('/program/admin/list_lookup_kinds');
  const getRows = usePost('/program/admin/get_lookup_rows');

  const [kinds, setKinds] = useState<string[]>([]);
  const [uploadKind, setUploadKind] = useState('');
  const [uploadRows, setUploadRows] = useState<ParsedRow[] | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<{ rowsProcessed: number, uniqueKeys: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedKind, setSelectedKind] = useState('');
  const [viewRows, setViewRows] = useState<ParsedRow[] | null>(null);
  const [viewTotal, setViewTotal] = useState(0);
  const [viewPage, setViewPage] = useState(1);
  const [viewError, setViewError] = useState<string | null>(null);
  const [isLoadingRows, setIsLoadingRows] = useState(false);
  const selectedKindRef = useRef(selectedKind);
  selectedKindRef.current = selectedKind;

  const refreshKinds = async () => {
    const { lookupKinds } = await listKinds({});
    setKinds(lookupKinds);
  };

  useEffect(() => {
    refreshKinds();
  }, []);

  const fetchRows = async (kind: string, page: number) => {
    if (!kind) {
      setViewRows(null);
      setViewTotal(0);
      setViewPage(1);
      setViewError(null);
      return;
    }
    setIsLoadingRows(true);
    setViewError(null);
    try {
      const res = await getRows({ kind, page, limit: VIEW_PAGE_LIMIT });
      if (kind !== selectedKindRef.current) return;
      setViewRows(res.rows);
      setViewTotal(res.total);
      setViewPage(res.page);
    } catch (err) {
      if (kind !== selectedKindRef.current) return;
      setViewError('Failed to load rows. Please try again.');
      setViewRows(null);
    } finally {
      if (kind === selectedKindRef.current) setIsLoadingRows(false);
    }
  };

  useEffect(() => {
    fetchRows(selectedKind, 1);
  }, [selectedKind]);

  const viewTotalPages = Math.ceil(viewTotal / VIEW_PAGE_LIMIT);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadRows(null);
    setParseError(null);
    setUploadError(null);
    setResult(null);

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors?.length) {
          setParseError(`Failed to parse CSV: ${results.errors[0].message}`);
          return;
        }
        const headers = (results.meta.fields ?? []).map(h => h.trim().toLowerCase());
        if (!headers.includes('key') || !headers.includes('value')) {
          setParseError('CSV must have "key" and "value" columns.');
          return;
        }
        const keyField = results.meta.fields!.find(h => h.trim().toLowerCase() === 'key')!;
        const valueField = results.meta.fields!.find(h => h.trim().toLowerCase() === 'value')!;

        const parsedRows = results.data
          .map(row => ({ key: (row[keyField] || '').trim(), value: (row[valueField] || '').trim() }))
          .filter(row => row.key && row.value);

        if (parsedRows.length === 0) {
          setParseError('No valid rows found in CSV (rows need a non-empty key and value).');
          return;
        }

        setUploadRows(parsedRows);
      },
      error: (err) => {
        setParseError(`Failed to parse CSV: ${err.message}`);
      },
    });
  };

  const uniqueKeyCount = useMemo(() => uploadRows ? new Set(uploadRows.map(r => r.key)).size : 0, [uploadRows]);
  const duplicateKeyCount = uploadRows ? uploadRows.length - uniqueKeyCount : 0;

  const handleUpload = async () => {
    if (!uploadKind.trim() || !uploadRows) return;
    setIsUploading(true);
    setUploadError(null);
    let res;
    try {
      res = await uploadLookup({ kind: uploadKind.trim(), rows: uploadRows });
    } catch (err) {
      setUploadError('Upload failed. Please try again.');
      setIsUploading(false);
      return;
    }

    setResult({ rowsProcessed: res.rowsProcessed, uniqueKeys: res.uniqueKeys });
    setUploadRows(null);
    if (fileInputRef.current) fileInputRef.current.value = '';

    try {
      await refreshKinds();
      if (uploadKind.trim() === selectedKind) {
        await fetchRows(selectedKind, viewPage);
      }
    } catch (err) {
      // Ignore - refresh is best effort
    }
    setIsUploading(false);
  };

  const canUpload = !!uploadKind.trim() && !!uploadRows && uploadRows.length > 0 && !isUploading;

  return (
    <div className="flex flex-col h-full justify-start p-2 max-w-2xl space-y-8">
      <div className="border border-gray-200 rounded-lg bg-white shadow-sm p-4">
        <label htmlFor="view-kind" className="block text-lg font-medium text-gray-700">
          View Lookup Kind
        </label>
        <select
          id="view-kind"
          value={selectedKind}
          onChange={(e) => setSelectedKind(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        >
          <option value="">Select a kind...</option>
          {kinds.map(k => <option key={k} value={k}>{k}</option>)}
        </select>

        {viewError && <p className="mt-2 text-sm text-red-600">{viewError}</p>}

        {selectedKind && !viewError && viewRows && (
          <div className={`mt-2 transition-opacity ${isLoadingRows ? 'opacity-50' : ''}`}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500">Key</th>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {viewRows.map((r) => (
                  <tr key={r.key}>
                    <td className="px-2 py-1 text-sm text-gray-900">{r.key}</td>
                    <td className="px-2 py-1 text-sm text-gray-900">{r.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {viewTotalPages > 1 && (
              <div className="mt-2 flex items-center gap-2 text-sm">
                <button
                  onClick={() => fetchRows(selectedKind, viewPage - 1)}
                  disabled={viewPage <= 1 || isLoadingRows}
                  className="rounded border px-2 py-1 disabled:opacity-50"
                >
                  Prev
                </button>
                <span>Page {viewPage} of {viewTotalPages} ({viewTotal} rows)</span>
                <button
                  onClick={() => fetchRows(selectedKind, viewPage + 1)}
                  disabled={viewPage >= viewTotalPages || isLoadingRows}
                  className="rounded border px-2 py-1 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {selectedKind && isLoadingRows && !viewRows && (
          <p className="mt-2 text-sm text-gray-500">Loading...</p>
        )}
      </div>

      <div className="border border-gray-200 rounded-lg bg-white shadow-sm p-4">
        <h3 className="text-lg font-medium text-gray-700 mb-2">Upload Lookup Table</h3>

        <label htmlFor="kind" className="block text-sm font-medium text-gray-700">
          Lookup Kind
        </label>
        <input
          type="text"
          name="kind"
          id="kind"
          value={uploadKind}
          onChange={(e) => setUploadKind(e.target.value)}
          placeholder="e.g. state_abbreviation"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        />

        <label htmlFor="csv" className="block text-sm font-medium text-gray-700 mt-4">
          CSV File (must have "key" and "value" columns)
        </label>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          className="mt-1 block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
        />

        {parseError && <p className="mt-2 text-sm text-red-600">{parseError}</p>}
        {uploadError && <p className="mt-2 text-sm text-red-600">{uploadError}</p>}

        {uploadRows && !parseError && (
          <div className="mt-2 text-sm text-gray-700">
            Parsed {uploadRows.length} rows ({uniqueKeyCount} unique keys).
          </div>
        )}

        {uploadRows && !parseError && duplicateKeyCount > 0 && (
          <p className="mt-2 text-sm text-red-600">
            Warning! {duplicateKeyCount} row{duplicateKeyCount > 1 ? 's have' : ' has'} a key that appears more than once.
            Values for the same key will be merged.
          </p>
        )}

        {result && (
          <div className="mt-2 text-sm text-green-700">
            Uploaded successfully: {result.rowsProcessed} rows processed, {result.uniqueKeys} unique keys.
          </div>
        )}

        <button
          onClick={handleUpload}
          disabled={!canUpload}
          className="mt-4 inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isUploading ? 'Uploading...' : 'Upload'}
        </button>
      </div>
    </div>
  );
}
